import React, { useRef, type ReactNode } from 'react';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import type {
  ComponentAppearance,
  ComponentSize,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';

/** Props for a button that opens the system file picker. */
interface FileButtonProps {
  /** Button content, usually an icon and a label. */
  children: ReactNode;
  /** Called with the chosen file. */
  onSelect: (file: File) => void;
  /** File types the picker offers, as in the input's `accept` attribute. */
  accept?: string;
  /** ID of the hidden file input, so a `label` can point at it. */
  inputId?: string;
  /** IDs of the elements that describe the input. */
  describedBy?: string;
  /** Whether the button is unavailable. */
  disabled?: boolean;
  /** Whether a selected file is being processed. */
  loading?: boolean;
  /** Button color variant. */
  variant?: ComponentVariant;
  /** Button appearance. */
  appearance?: ComponentAppearance;
  /** Button size. */
  size?: ComponentSize;
  /** Extra classes for the button. */
  className?: string;
  /** Accessible name, when the visible content is shorter than the action. */
  ariaLabel?: string;
}

/**
 * Button that opens the system file picker and hands over the chosen file.
 * The parent validates the file. Use it where a file picker sits next to
 * other actions, such as changing an image that already has a preview;
 * `FileInput` suits form fields that show the file name.
 *
 * @param props - Content, picker options, state and button styling.
 * @returns The hidden file input and its button.
 */
export const FileButton: React.FC<FileButtonProps> = ({
  children,
  onSelect,
  accept,
  inputId,
  describedBy,
  disabled = false,
  loading = false,
  variant = 'secondary',
  appearance = 'outline',
  size = 'sm',
  className,
  ariaLabel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        className="hidden"
        aria-describedby={describedBy}
        disabled={disabled || loading}
        onChange={(event) => {
          const file: File | undefined = event.target.files?.[0];
          // Reset so choosing the same file again still fires a change.
          event.target.value = '';
          if (file) onSelect(file);
        }}
      />
      <Button
        type="button"
        variant={variant}
        appearance={appearance}
        size={size}
        disabled={disabled}
        loading={loading}
        className={className}
        aria-label={ariaLabel}
        onClick={() => inputRef.current?.click()}
      >
        {children}
      </Button>
    </>
  );
};
